import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { TrendingUp, MousePointer, Eye, Wallet, Link2, Unplug, RefreshCw, Copy, ExternalLink, AlertCircle, Lock, Sparkles } from "lucide-react";

const fmtEur = (n) => (Number(n) || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });
const fmtInt = (n) => (Number(n) || 0).toLocaleString("fr-FR");

const inp = "w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-orange/60 font-mono";

export const AdSenseRevenueSection = () => {
  const [status, setStatus] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(30);
  const [showCreds, setShowCreds] = useState(false);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/admin/adsense/status");
      setStatus(data);
      return data;
    } catch { return null; }
  };
  const loadRevenue = async (d = days) => {
    try {
      const { data } = await api.get(`/admin/adsense/revenue?days=${d}`);
      setRevenue(data);
    } catch { /* silent: empty state */ }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const s = await loadStatus();
      if (s?.connected) await loadRevenue(days);
      setLoading(false);
    })();
    // OAuth retour de Google
    const url = new URL(window.location.href);
    if (url.searchParams.get("adsense_connected") === "1") {
      toast.success("AdSense connecté avec succès 🎉");
      url.searchParams.delete("adsense_connected");
      window.history.replaceState({}, "", url.toString());
    }
    const errMsg = url.searchParams.get("adsense_error");
    if (errMsg) {
      toast.error("Échec OAuth: " + errMsg);
      url.searchParams.delete("adsense_error");
      window.history.replaceState({}, "", url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveCreds = async (e) => {
    e?.preventDefault();
    if (!clientId || !clientSecret) { toast.error("Client ID et Secret requis"); return; }
    setBusy(true);
    try {
      await api.post("/admin/adsense/credentials", { client_id: clientId, client_secret: clientSecret });
      toast.success("Credentials enregistrés");
      setShowCreds(false); setClientSecret("");
      await loadStatus();
    } catch (e) { toast.error(e?.response?.data?.error || "Erreur"); }
    finally { setBusy(false); }
  };

  const startOAuth = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/adsense/oauth/start");
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erreur OAuth");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Déconnecter AdSense ? Les rapports en cache seront supprimés.")) return;
    setBusy(true);
    try {
      await api.post("/admin/adsense/disconnect");
      toast.success("Déconnecté");
      setStatus(null); setRevenue(null);
      await loadStatus();
    } catch { toast.error("Erreur"); }
    finally { setBusy(false); }
  };

  const refresh = async () => {
    setBusy(true);
    await loadRevenue(days);
    setBusy(false);
    toast.success("Données rafraîchies");
  };

  const copy = (val) => { navigator.clipboard?.writeText(val); toast.success("Copié"); };

  if (loading) return <div className="text-white/50 text-sm">Chargement…</div>;

  // -------- Pas encore configuré : assistant 3 étapes --------
  if (!status?.has_credentials) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-5" data-testid="adsense-onboarding">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-brand-orange to-brand-red flex items-center justify-center"><Sparkles size={18} /></div>
          <div>
            <h3 className="font-display text-xl font-black">Connecter Google AdSense</h3>
            <p className="text-xs text-white/55 mt-0.5">Affiche tes revenus en temps réel directement dans Voamada (lecture seule, jamais d'écriture).</p>
          </div>
        </div>

        <ol className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="shrink-0 w-7 h-7 rounded-full bg-brand-orange/20 text-brand-orange flex items-center justify-center font-bold">1</span>
            <div>
              <p className="font-bold">Crée un projet Google Cloud</p>
              <p className="text-xs text-white/55">Va sur <a className="text-brand-orange hover:underline inline-flex items-center gap-1" href="https://console.cloud.google.com/projectcreate" target="_blank" rel="noopener noreferrer">Google Cloud Console <ExternalLink size={11} /></a> et crée un projet (ex : <code>voamada-adsense</code>)</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="shrink-0 w-7 h-7 rounded-full bg-brand-orange/20 text-brand-orange flex items-center justify-center font-bold">2</span>
            <div>
              <p className="font-bold">Active l'AdSense Management API</p>
              <p className="text-xs text-white/55">Dans le menu → <strong>API et services</strong> → <strong>Bibliothèque</strong> → cherche "AdSense Management API" → <strong>Activer</strong></p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="shrink-0 w-7 h-7 rounded-full bg-brand-orange/20 text-brand-orange flex items-center justify-center font-bold">3</span>
            <div>
              <p className="font-bold">Crée des identifiants OAuth 2.0</p>
              <p className="text-xs text-white/55 mb-2"><strong>API et services → Identifiants → Créer → ID client OAuth → Application Web</strong></p>
              <div className="rounded-lg bg-black/40 border border-white/10 p-3 text-xs space-y-2">
                <p className="text-white/60">Dans <strong>"URI de redirection autorisés"</strong>, colle exactement :</p>
                <div className="flex gap-2">
                  <code className="flex-1 bg-black/50 px-2 py-1.5 rounded text-brand-orange font-mono text-[11px] break-all" data-testid="oauth-redirect-uri">{status?.redirect_uri || "https://www.voamada.com/api/adsense/oauth/callback"}</code>
                  <button onClick={() => copy(status?.redirect_uri || "")} className="btn-ghost px-2 py-1 text-xs inline-flex items-center gap-1" data-testid="copy-redirect-uri"><Copy size={12} /> Copier</button>
                </div>
                <p className="text-white/55">Puis copie le <strong>Client ID</strong> et le <strong>Client Secret</strong> ci-dessous :</p>
              </div>
            </div>
          </li>
        </ol>

        <form onSubmit={saveCreds} className="space-y-3 border-t border-white/10 pt-5">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/45 font-bold block mb-1">Client ID</label>
            <input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="xxxxx.apps.googleusercontent.com" className={inp} data-testid="adsense-client-id" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/45 font-bold block mb-1">Client Secret</label>
            <input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="GOCSPX-xxxx" className={inp} data-testid="adsense-client-secret" />
            <p className="text-[10px] text-white/40 mt-1 inline-flex items-center gap-1"><Lock size={10} /> Stocké chiffré côté serveur, jamais exposé côté client.</p>
          </div>
          <button type="submit" disabled={busy} className="btn-primary text-sm disabled:opacity-50" data-testid="adsense-save-creds">
            {busy ? "Enregistrement…" : "Enregistrer & continuer"}
          </button>
        </form>
      </div>
    );
  }

  // -------- Credentials saisis mais pas encore autorisé --------
  if (!status?.connected) {
    return (
      <div className="rounded-2xl border border-brand-orange/30 bg-brand-orange/5 p-5 space-y-3" data-testid="adsense-needs-auth">
        <div className="flex items-start gap-3">
          <AlertCircle size={20} className="text-brand-orange shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-bold">Credentials enregistrés ✓</h3>
            <p className="text-sm text-white/70 mt-1">Il reste à autoriser Voamada à lire tes données AdSense. Clique pour ouvrir Google.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={startOAuth} disabled={busy} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="adsense-oauth-start">
            <Link2 size={14} /> {busy ? "Redirection…" : "Autoriser Voamada (Google)"}
          </button>
          <button onClick={() => setShowCreds(!showCreds)} className="btn-ghost text-sm" data-testid="adsense-edit-creds">
            Modifier les credentials
          </button>
        </div>
        {showCreds && (
          <form onSubmit={saveCreds} className="space-y-2 pt-3 border-t border-white/10">
            <input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="Nouveau Client ID" className={inp} />
            <input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="Nouveau Client Secret" className={inp} />
            <button type="submit" disabled={busy} className="btn-primary text-sm">Enregistrer</button>
          </form>
        )}
      </div>
    );
  }

  // -------- Connecté : Dashboard --------
  return (
    <div className="space-y-5" data-testid="adsense-revenue-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-xs font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Connecté
          </span>
          <span className="text-xs text-white/45">Compte : <code className="text-brand-orange">{status.account_id}</code></span>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => { setDays(+e.target.value); loadRevenue(+e.target.value); }} className="rounded-lg bg-white/[0.04] border border-white/10 px-2 py-1.5 text-xs" data-testid="adsense-period">
            <option value="7">7 derniers jours</option>
            <option value="30">30 derniers jours</option>
            <option value="90">90 derniers jours</option>
          </select>
          <button onClick={refresh} disabled={busy} className="btn-ghost text-xs px-2.5 py-1.5 inline-flex items-center gap-1" data-testid="adsense-refresh">
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Rafraîchir
          </button>
          <button onClick={disconnect} disabled={busy} className="btn-ghost text-xs px-2.5 py-1.5 inline-flex items-center gap-1 text-brand-red hover:bg-brand-red/10" data-testid="adsense-disconnect">
            <Unplug size={12} /> Déconnecter
          </button>
        </div>
      </div>

      {/* Empty state si pas de données */}
      {(!revenue?.days || revenue.days.length === 0) ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center">
          <div className="text-5xl mb-2">⏳</div>
          <p className="font-bold">Pas encore de données AdSense</p>
          <p className="text-sm text-white/55 mt-1 max-w-md mx-auto">
            AdSense met 24-48h à fournir les premiers chiffres après la diffusion d'annonces.
            Reviens demain — la synchro est automatique.
          </p>
        </div>
      ) : (
        <>
          {/* Stats cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={Wallet}      label={`Revenus ${days}j`}    value={fmtEur(revenue.totals.earnings)}  accent="emerald" testid="stat-earnings" />
            <StatCard icon={Eye}         label="Impressions"           value={fmtInt(revenue.totals.impressions)} testid="stat-impressions" />
            <StatCard icon={MousePointer} label="Clics"                value={fmtInt(revenue.totals.clicks)} testid="stat-clicks" />
            <StatCard icon={TrendingUp}  label="CTR / eCPM"            value={`${revenue.totals.ctr}% · ${fmtEur(revenue.totals.ecpm)}`} testid="stat-ctr" />
          </div>

          {/* Today / Yesterday quick view */}
          <div className="grid md:grid-cols-2 gap-3">
            <DayCard title="Aujourd'hui" data={revenue.breakdown.today} testid="day-today" />
            <DayCard title="Hier" data={revenue.breakdown.yesterday} testid="day-yesterday" />
          </div>

          {/* Chart */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <p className="text-xs uppercase tracking-widest text-white/45 font-bold mb-4">Revenus quotidiens — {days} derniers jours</p>
            <RevenueChart data={revenue.days} />
          </div>
        </>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, accent = "default", testid }) => {
  const cls = accent === "emerald"
    ? "from-emerald-500/15 to-emerald-500/5 border-emerald-500/25 text-emerald-200"
    : "from-white/[0.05] to-white/[0.01] border-white/10 text-white";
  return (
    <div className={`rounded-2xl border bg-gradient-to-br ${cls} p-4`} data-testid={testid}>
      <div className="flex items-center gap-2 text-white/55 text-[10px] uppercase tracking-widest font-bold mb-2">
        <Icon size={12} /> {label}
      </div>
      <p className="font-display text-2xl md:text-3xl font-black tabular-nums leading-tight">{value}</p>
    </div>
  );
};

const DayCard = ({ title, data, testid }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <p className="text-[10px] uppercase tracking-widest text-white/45 font-bold">{title}</p>
      {data && <p className="text-[11px] text-white/40">{new Date(data.day).toLocaleDateString("fr-FR")}</p>}
    </div>
    {!data ? <p className="text-white/40 text-sm">Pas encore de données</p> : (
      <div className="grid grid-cols-3 gap-2">
        <Mini label="Revenus" value={fmtEur(data.earnings_usd || data.earnings || 0)} highlight />
        <Mini label="Impressions" value={fmtInt(data.impressions)} />
        <Mini label="Clics" value={fmtInt(data.clicks)} />
      </div>
    )}
  </div>
);

const Mini = ({ label, value, highlight }) => (
  <div>
    <p className={`font-display font-black text-lg tabular-nums leading-tight ${highlight ? "text-brand-yellow" : ""}`}>{value}</p>
    <p className="text-[10px] uppercase tracking-widest text-white/40 font-bold mt-0.5">{label}</p>
  </div>
);

const RevenueChart = ({ data }) => {
  const ref = useRef(null);
  const [w, setW] = useState(800);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((entries) => setW(entries[0].contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const max = useMemo(() => Math.max(0.001, ...data.map((d) => +d.earnings_usd || +d.earnings || 0)), [data]);
  const h = 200;
  const padX = 10, padY = 20;
  const innerW = Math.max(50, w - padX * 2);

  if (!data?.length) return null;

  const points = data.map((d, i) => {
    const x = padX + (i / Math.max(1, data.length - 1)) * innerW;
    const v = +d.earnings_usd || +d.earnings || 0;
    const y = h - padY - (v / max) * (h - padY * 2);
    return { x, y, day: d.day, v };
  });

  const path = points.map((p, i) => (i === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`)).join(" ");
  const area = path + ` L${points[points.length - 1].x},${h - padY} L${padX},${h - padY} Z`;

  return (
    <div ref={ref} className="w-full overflow-hidden" data-testid="revenue-chart">
      <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} className="block">
        <defs>
          <linearGradient id="rev-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor="#FF8A4C" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#FF8A4C" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
          <line key={i} x1={padX} x2={w - padX} y1={padY + p * (h - padY * 2)} y2={padY + p * (h - padY * 2)} stroke="rgba(255,255,255,0.06)" strokeDasharray="2 4" />
        ))}
        <path d={area} fill="url(#rev-grad)" />
        <path d={path} fill="none" stroke="#FF8A4C" strokeWidth="2" />
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="2.5" fill="#FF8A4C" />
            <title>{`${new Date(p.day).toLocaleDateString("fr-FR")} : ${fmtEur(p.v)}`}</title>
          </g>
        ))}
      </svg>
      <div className="flex items-center justify-between text-[10px] text-white/40 mt-1">
        <span>{new Date(data[0].day).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span>
        <span>{fmtEur(max)} max</span>
        <span>{new Date(data[data.length - 1].day).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span>
      </div>
    </div>
  );
};

export default AdSenseRevenueSection;
